import { Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"
import { useMemo, useRef, useState } from "react"
import { Button } from "../../components/ui/button"
import { Checkbox } from "../../components/ui/checkbox"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"

export type CompanyOmieOption = {
  id: number
  name: string
  cnpj: string
  omie_servicos: { id: number; label: string }[]
  omie_contas_correntes: { id: number; label: string }[]
}

export type FiscalSettingFormValues = {
  id?: number
  municipio_prestacao: string
  descricao_contrato: string
  irrf: string
  pis: string
  cofins: string
  iss: string
  csll: string
  irrf_withhold: boolean
  pis_withhold: boolean
  cofins_withhold: boolean
  iss_withhold: boolean
  csll_withhold: boolean
  numero_contrato: string
  descricao_servicos: string
  codigo_servico_nota: string
  omie_c_cod_serv_mun: string
  omie_c_cod_serv_lc116: string
  company_id?: number | null
  omie_servico_id?: number | null
  omie_conta_corrente_id?: number | null
}

type FiscalSettingFormProps = {
  initialValues: FiscalSettingFormValues
  companies: CompanyOmieOption[]
  submitUrl: string
  submitLabel: string
  method: "post" | "patch"
  errors?: Record<string, string[]>
  cancelHref: string
}

const TEMPLATE_VARIABLES: Record<string, { key: string; label: string }[]> = {
  "Nota Fiscal": [
    { key: "medicao", label: "Número da medição" },
    { key: "periodo_inicio", label: "Início do período (dd/mm/aaaa)" },
    { key: "periodo_fim", label: "Fim do período (dd/mm/aaaa)" },
    { key: "municipio", label: "Município da nota" },
    { key: "codigo_sap", label: "Código SAP" },
    { key: "cr", label: "CR" },
    { key: "pedido_compras", label: "Pedido de compras" },
    { key: "data_vencimento", label: "Data de vencimento (dd/mm/aaaa)" },
    { key: "valor", label: "Valor da nota (R$ formatado)" },
    { key: "valor_numero", label: "Valor (somente número)" },
    { key: "tributos", label: "Total de tributos (R$ formatado)" },
    { key: "discriminacao", label: "Discriminação (do TXT)" }
  ],
  "Cliente / Tomador": [
    { key: "cliente", label: "Nome do cliente" },
    { key: "razao_social", label: "Razão social" },
    { key: "cpf_cnpj", label: "CPF/CNPJ" },
    { key: "inscricao_municipal", label: "Inscrição municipal" },
    { key: "inscricao_estadual", label: "Inscrição estadual" },
    { key: "endereco", label: "Endereço completo" },
    { key: "bairro", label: "Bairro" },
    { key: "cidade_tomador", label: "Cidade" },
    { key: "uf_tomador", label: "UF" },
    { key: "cep", label: "CEP" },
    { key: "codigo_sap_cliente", label: "Código SAP do cliente" },
    { key: "cr_cliente", label: "CR do cliente" }
  ],
  "Config Fiscal": [
    { key: "numero_contrato", label: "Número do contrato" },
    { key: "descricao_contrato", label: "Descrição do contrato" },
    { key: "municipio_prestacao", label: "Município de prestação" },
    { key: "irrf", label: "Alíquota IRRF (%)" },
    { key: "pis", label: "Alíquota PIS (%)" },
    { key: "cofins", label: "Alíquota COFINS (%)" },
    { key: "csll", label: "Alíquota CSLL (%)" },
    { key: "iss", label: "Alíquota ISS (%)" }
  ],
  Empresa: [
    { key: "empresa", label: "Nome da empresa emissora" },
    { key: "empresa_cnpj", label: "CNPJ da empresa emissora" }
  ]
}

function buildPreview(template: string, formValues: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => {
    const lower = key.toLowerCase()
    return formValues[lower] ?? `{{${key}}}`
  })
}

function ErrorText({ value }: { value?: string[] }) {
  if (!value?.length) return null
  return <p className="text-xs text-rose-400">{value[0]}</p>
}

const EXAMPLE_TEMPLATE = `Contrato nº {{numero_contrato}}
{{descricao_contrato}}
Medição: {{medicao}}ª Medição
Período: {{periodo_inicio}} - {{periodo_fim}}
Município: {{municipio}}
Pedido de Compras: {{pedido_compras}}
RC: {{cr}}
Reter os impostos: IRRF {{irrf}}% - PIS {{pis}}% - COFINS {{cofins}}% - CSLL {{csll}}% - ISS {{iss}}%
Vencimento: {{data_vencimento}}
COD SAP - {{codigo_sap}} CR {{cr}}`

function DescricaoServicosEditor({
  value,
  onChange,
  formValues,
  errors
}: {
  value: string
  onChange: (val: string) => void
  formValues: Record<string, string>
  errors?: string[]
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [showVars, setShowVars] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  function insertVariable(key: string) {
    const el = textareaRef.current
    if (!el) {
      onChange(value + `{{${key}}}`)
      return
    }
    const start = el.selectionStart
    const end = el.selectionEnd
    const tag = `{{${key}}}`
    const newVal = value.substring(0, start) + tag + value.substring(end)
    onChange(newVal)
    requestAnimationFrame(() => {
      el.focus()
      el.selectionStart = start + tag.length
      el.selectionEnd = start + tag.length
    })
  }

  function loadExample() {
    if (value.trim() && !window.confirm("Isso substituirá o texto atual. Deseja continuar?")) return
    onChange(EXAMPLE_TEMPLATE)
  }

  const previewText = buildPreview(value, formValues)
  const hasTemplate = /\{\{\w+\}\}/.test(value)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setShowVars(!showVars)}
          className="rounded border border-indigo-700/50 bg-indigo-950/40 px-2.5 py-1 text-xs font-medium text-indigo-300 transition-colors hover:bg-indigo-900/50"
        >
          {showVars ? "Ocultar variáveis" : "Ver variáveis disponíveis"}
        </button>
        <button
          type="button"
          onClick={loadExample}
          className="rounded border border-amber-700/50 bg-amber-950/40 px-2.5 py-1 text-xs font-medium text-amber-300 transition-colors hover:bg-amber-900/50"
        >
          Carregar modelo de exemplo
        </button>
        {hasTemplate && (
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="rounded border border-emerald-700/50 bg-emerald-950/40 px-2.5 py-1 text-xs font-medium text-emerald-300 transition-colors hover:bg-emerald-900/50"
          >
            {showPreview ? "Ocultar preview" : "Preview"}
          </button>
        )}
      </div>

      {showVars && (
        <div className="rounded-lg border border-slate-700 bg-slate-950/80 p-4">
          <p className="mb-3 text-xs text-slate-400">
            Clique em uma variável para inserir na posição do cursor. Ao enviar para Omie, cada <code className="text-amber-400/80">{`{{variavel}}`}</code> será substituída pelo valor real.
          </p>
          <div className="space-y-4">
            {Object.entries(TEMPLATE_VARIABLES).map(([group, vars]) => (
              <div key={group}>
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">{group}</p>
                <div className="overflow-hidden rounded-md border border-slate-700">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-700 bg-slate-900/80">
                        <th className="px-3 py-1.5 text-left font-medium text-slate-400">Variável</th>
                        <th className="px-3 py-1.5 text-left font-medium text-slate-400">Descrição</th>
                        <th className="w-16 px-3 py-1.5 text-right font-medium text-slate-400" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {vars.map((v) => (
                        <tr key={v.key} className="hover:bg-slate-800/50">
                          <td className="px-3 py-1.5 font-mono text-amber-300">{`{{${v.key}}}`}</td>
                          <td className="px-3 py-1.5 text-slate-300">{v.label}</td>
                          <td className="px-3 py-1.5 text-right">
                            <button
                              type="button"
                              onClick={() => insertVariable(v.key)}
                              className="rounded bg-slate-700 px-2 py-0.5 text-xs text-slate-200 transition-colors hover:bg-indigo-700 hover:text-white"
                            >
                              Inserir
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <textarea
        ref={textareaRef}
        id="descricao_servicos"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={10}
        placeholder={"Contrato nº {{numero_contrato}}\n{{descricao_contrato}}\nMedição: {{medicao}}ª Medição\nPeríodo: {{periodo_inicio}} - {{periodo_fim}}\nMunicípio: {{municipio}}\nPedido de Compras: {{pedido_compras}}\nRC: {{cr}}"}
        className="flex min-h-24 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-sm text-white placeholder:text-slate-600"
      />
      <ErrorText value={errors} />

      {showPreview && hasTemplate && (
        <div className="rounded-lg border border-emerald-800/50 bg-emerald-950/20 p-3">
          <p className="mb-2 text-xs font-semibold text-emerald-400">Preview (valores da config fiscal já preenchidos, demais serão preenchidos ao gerar a nota):</p>
          <pre className="whitespace-pre-wrap rounded bg-slate-950/60 p-3 text-xs leading-relaxed text-slate-300">{previewText}</pre>
        </div>
      )}

      <p className="text-xs text-slate-500">
        Use <code className="text-amber-400/80">{`{{nome_variavel}}`}</code> para inserir valores dinâmicos.
        Textos fixos (banco, agência, etc.) podem ser digitados normalmente.
        Variáveis da nota (medição, período, valor, etc.) são preenchidas automaticamente ao enviar para Omie.
      </p>
    </div>
  )
}

export function FiscalSettingForm({
  initialValues,
  companies,
  submitUrl,
  submitLabel,
  method,
  errors,
  cancelHref
}: FiscalSettingFormProps) {
  const form = useForm({
    company_fiscal_setting: {
      municipio_prestacao: initialValues.municipio_prestacao ?? "",
      descricao_contrato: initialValues.descricao_contrato ?? "",
      irrf: initialValues.irrf ?? "",
      pis: initialValues.pis ?? "",
      cofins: initialValues.cofins ?? "",
      iss: initialValues.iss ?? "",
      csll: initialValues.csll ?? "",
      irrf_withhold: Boolean(initialValues.irrf_withhold),
      pis_withhold: Boolean(initialValues.pis_withhold),
      cofins_withhold: Boolean(initialValues.cofins_withhold),
      iss_withhold: Boolean(initialValues.iss_withhold),
      csll_withhold: Boolean(initialValues.csll_withhold),
      numero_contrato: initialValues.numero_contrato ?? "",
      descricao_servicos: initialValues.descricao_servicos ?? "",
      codigo_servico_nota: initialValues.codigo_servico_nota ?? "",
      omie_c_cod_serv_mun: initialValues.omie_c_cod_serv_mun ?? "",
      omie_c_cod_serv_lc116: initialValues.omie_c_cod_serv_lc116 ?? "",
      company_id: initialValues.company_id != null ? String(initialValues.company_id) : "",
      omie_servico_id: initialValues.omie_servico_id != null ? String(initialValues.omie_servico_id) : "",
      omie_conta_corrente_id: initialValues.omie_conta_corrente_id != null ? String(initialValues.omie_conta_corrente_id) : ""
    }
  })

  const f = form.data.company_fiscal_setting

  const selectedCompany = useMemo(
    () => companies.find((c) => String(c.id) === f.company_id),
    [companies, f.company_id]
  )

  function update<K extends keyof typeof f>(field: K, value: (typeof f)[K]) {
    form.setData("company_fiscal_setting", { ...f, [field]: value })
  }

  function onCompanyChange(companyId: string) {
    form.setData("company_fiscal_setting", {
      ...f,
      company_id: companyId,
      omie_servico_id: "",
      omie_conta_corrente_id: ""
    })
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (method === "patch") {
      form.patch(submitUrl)
      return
    }
    form.post(submitUrl)
  }

  return (
    <form onSubmit={submit} className="space-y-6 rounded-xl border border-slate-800 bg-slate-900 p-6">
      <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-4 md:col-span-2">
        <h3 className="mb-3 text-sm font-medium text-slate-200">Empresa (Omie) e vínculos</h3>
        <p className="mb-4 text-xs text-slate-500">
          Escolha a empresa cujas credenciais Omie serão usadas e vincule o serviço e a conta corrente sincronizados
          (<code className="text-slate-400">bin/rails omie:sync</code>). Obrigatório se quiser gravar serviço ou conta.
        </p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="company_id_fiscal">Empresa</Label>
            <select
              id="company_id_fiscal"
              value={f.company_id}
              onChange={(e) => onCompanyChange(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
            >
              <option value="">Selecione a empresa</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.cnpj})
                </option>
              ))}
            </select>
            <ErrorText value={errors?.company_id} />
          </div>
          <div className="space-y-2 md:col-span-1">
            <Label htmlFor="omie_servico_id">Serviço Omie (nCodServico)</Label>
            <select
              id="omie_servico_id"
              value={f.omie_servico_id}
              disabled={!f.company_id}
              onChange={(e) => update("omie_servico_id", e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 disabled:opacity-50"
            >
              <option value="">{f.company_id ? "Selecione o serviço" : "Selecione a empresa primeiro"}</option>
              {(selectedCompany?.omie_servicos ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
            <ErrorText value={errors?.omie_servico_id} />
            {f.company_id && (selectedCompany?.omie_servicos?.length ?? 0) === 0 && (
              <p className="text-xs text-amber-500/90">Nenhum serviço sincronizado para esta empresa.</p>
            )}
          </div>
          <div className="space-y-2 md:col-span-1">
            <Label htmlFor="omie_conta_corrente_id">Conta corrente Omie (nCodCC)</Label>
            <select
              id="omie_conta_corrente_id"
              value={f.omie_conta_corrente_id}
              disabled={!f.company_id}
              onChange={(e) => update("omie_conta_corrente_id", e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 disabled:opacity-50"
            >
              <option value="">{f.company_id ? "Selecione a conta" : "Selecione a empresa primeiro"}</option>
              {(selectedCompany?.omie_contas_correntes ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
            <ErrorText value={errors?.omie_conta_corrente_id} />
            {f.company_id && (selectedCompany?.omie_contas_correntes?.length ?? 0) === 0 && (
              <p className="text-xs text-amber-500/90">Nenhuma conta sincronizada para esta empresa.</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="municipio_prestacao">Município de prestação</Label>
          <Input
            id="municipio_prestacao"
            value={f.municipio_prestacao}
            onChange={(e) => update("municipio_prestacao", e.target.value)}
            className="border-slate-700 bg-slate-950 text-white"
          />
          <ErrorText value={errors?.municipio_prestacao} />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="descricao_contrato">Descrição do contrato</Label>
          <textarea
            id="descricao_contrato"
            value={f.descricao_contrato}
            onChange={(e) => update("descricao_contrato", e.target.value)}
            rows={3}
            className="flex min-h-16 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
          />
          <ErrorText value={errors?.descricao_contrato} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="numero_contrato">Número do contrato</Label>
          <Input
            id="numero_contrato"
            value={f.numero_contrato}
            onChange={(e) => update("numero_contrato", e.target.value)}
            className="border-slate-700 bg-slate-950 text-white"
          />
          <ErrorText value={errors?.numero_contrato} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="codigo_servico_nota">Código do serviço (NF) *</Label>
          <Input
            id="codigo_servico_nota"
            value={f.codigo_servico_nota}
            onChange={(e) => update("codigo_servico_nota", e.target.value)}
            className="border-slate-700 bg-slate-950 text-white"
            required
          />
          <ErrorText value={errors?.codigo_servico_nota} />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="omie_c_cod_serv_mun">cCodServMun (Omie)</Label>
          <Input
            id="omie_c_cod_serv_mun"
            value={f.omie_c_cod_serv_mun}
            onChange={(e) => update("omie_c_cod_serv_mun", e.target.value)}
            placeholder="Código do serviço municipal na Omie"
            className="border-slate-700 bg-slate-950 font-mono text-sm text-white"
          />
          <ErrorText value={errors?.omie_c_cod_serv_mun} />
          <p className="text-xs text-slate-500">Se vazio, usa o &quot;Código do serviço (NF)&quot; acima ao montar o payload IncluirOS.</p>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="omie_c_cod_serv_lc116">cCodServLC116 (Omie, LC 116)</Label>
          <Input
            id="omie_c_cod_serv_lc116"
            value={f.omie_c_cod_serv_lc116}
            onChange={(e) => update("omie_c_cod_serv_lc116", e.target.value)}
            placeholder="Ex.: 17.02"
            className="border-slate-700 bg-slate-950 font-mono text-sm text-white"
          />
          <ErrorText value={errors?.omie_c_cod_serv_lc116} />
          <p className="text-xs text-slate-500">Se vazio, usa o padrão da aplicação (ENV OMIE_OS_SERVICE_LC116).</p>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="descricao_servicos">Descrição dos serviços</Label>
          <DescricaoServicosEditor
            value={f.descricao_servicos}
            onChange={(val) => update("descricao_servicos", val)}
            formValues={{
              numero_contrato: f.numero_contrato,
              descricao_contrato: f.descricao_contrato,
              municipio_prestacao: f.municipio_prestacao,
              irrf: f.irrf,
              pis: f.pis,
              cofins: f.cofins,
              csll: f.csll,
              iss: f.iss
            }}
            errors={errors?.descricao_servicos}
          />
        </div>
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-4">
        <h3 className="mb-4 text-sm font-medium text-slate-200">Tributos (%)</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(
            [
              ["irrf", "IRRF", "irrf_withhold"] as const,
              ["pis", "PIS", "pis_withhold"] as const,
              ["cofins", "COFINS", "cofins_withhold"] as const,
              ["iss", "ISS", "iss_withhold"] as const,
              ["csll", "CSLL", "csll_withhold"] as const
            ] as const
          ).map(([field, label, withhold]) => (
            <div key={field} className="space-y-2 rounded-md border border-slate-800 bg-slate-900 p-3">
              <Label htmlFor={field}>{label} (%)</Label>
              <Input
                id={field}
                type="number"
                step="0.0001"
                min="0"
                max="100"
                value={f[field]}
                onChange={(e) => update(field, e.target.value)}
                className="border-slate-700 bg-slate-950 text-white"
              />
              <div className="flex items-center gap-2">
                <Checkbox
                  id={withhold}
                  checked={f[withhold]}
                  onChange={(e) => update(withhold, e.target.checked)}
                />
                <Label htmlFor={withhold} className="cursor-pointer text-xs font-normal text-slate-300">
                  Reter {label}
                </Label>
              </div>
              <ErrorText value={errors?.[field]} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Button asChild type="button" variant="outline" className="border-slate-700 bg-transparent">
          <Link href={cancelHref}>Cancelar</Link>
        </Button>
        <Button type="submit" disabled={form.processing}>
          {form.processing ? "Salvando..." : submitLabel}
        </Button>
      </div>
    </form>
  )
}
